import { Capacitor } from '@capacitor/core'
import { PushNotifications } from '@capacitor/push-notifications'
import { useCallback, useEffect, useState } from 'react'

import { useAppState } from './useAppState'

import type { PermissionStatus } from '@capacitor/push-notifications'

function usePushNotifications() {
  const { active } = useAppState()
  const [permissions, setPermissions] = useState<PermissionStatus>()

  // Cached methods
  // ------------------------------------------------------------------------- /
  const checkPermissions = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      const permissionsValue = await PushNotifications.checkPermissions()
      setPermissions(permissionsValue)

      return permissionsValue
    }
  }, [setPermissions])

  const requestPermissions = useCallback(async () => {
    if (Capacitor.isNativePlatform()) {
      const permissions = await checkPermissions()

      if (permissions?.receive !== 'granted') {
        const request = await PushNotifications.requestPermissions()
        if (request.receive === 'denied') {
          return false
        }
      }

      if (permissions?.receive === 'granted') {
        await PushNotifications.register()
      }
    }
  }, [checkPermissions])

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (Capacitor.isNativePlatform() && active) {
      // Revalidate permissions when the app becomes active
      checkPermissions()
    }
  }, [active, checkPermissions])

  return {
    permissions,
    permissionGranted: permissions?.receive === 'granted',

    checkPermissions,
    requestPermissions,
  }
}

export { usePushNotifications }
